import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, Instagram, MapPin } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { COMPANY } from "../config/company";
import { useLocalizedHref } from "../hooks/useLocalizedSEO";
import { CITIES, cityPath } from "../lib/cities";

export default function Footer() {
  const { lang } = useLang();
  const loc = useLocation();
  const lh = useLocalizedHref();
  if (loc.pathname.startsWith("/m/")) return null;

  const t = (k) => ({
    impressum: { de: "Impressum", en: "Legal Notice", tr: "Künye" },
    privacy: { de: "Datenschutz", en: "Privacy", tr: "Gizlilik" },
    pricing: { de: "Preise", en: "Pricing", tr: "Fiyatlar" },
    login: { de: "Anmelden", en: "Sign in", tr: "Giriş" },
    contact: { de: "Kontakt aufnehmen", en: "Get in touch", tr: "İletişime geç" },
    tagline: { de: "Digitale QR-Menüs für moderne Gastronomie", en: "Digital QR menus for modern hospitality", tr: "Modern gastronomi için dijital QR menüler" },
    contactTitle: { de: "Kontakt", en: "Contact", tr: "İletişim" },
    sendEmail: { de: "E-Mail senden", en: "Send email", tr: "E-posta gönder" },
    instagramFollow: { de: "Instagram folgen", en: "Follow on Instagram", tr: "Instagram'da takip et" },
  })[k]?.[lang] || k;

  const mailto = `mailto:${COMPANY.publicEmail}`;
  // Show the first 8 cities — each linked to its single canonical URL.
  const FOOTER_CITIES = CITIES.slice(0, 8);
  const citiesLabel = { de: "Städte", en: "Cities", tr: "Şehirler" }[lang] || "Cities";

  return (
    <footer className="border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 mt-12" data-testid="site-footer">
      <div className="container mx-auto px-6 lg:px-12 py-10">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr_1fr_1fr]">
          <div>
            <div className="font-bold text-lg">{COMPANY.brand}</div>
            <div className="text-sm text-zinc-500 dark:text-zinc-400 mt-1.5 max-w-sm leading-relaxed">{t("tagline")}</div>
            <div className="mt-5 flex gap-2">
              <a
                href={mailto}
                aria-label="Email"
                title={t("sendEmail")}
                className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 grid place-items-center hover:bg-purple-600 hover:text-white transition-all duration-300 hover:scale-110"
                data-testid="footer-email"
              >
                <Mail className="h-4 w-4" />
              </a>
              <a
                href={COMPANY.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                title={t("instagramFollow")}
                className="w-10 h-10 rounded-xl bg-pink-500/10 text-pink-600 dark:text-pink-400 grid place-items-center hover:bg-gradient-to-tr hover:from-[#f09433] hover:via-[#dc2743] hover:to-[#bc1888] hover:text-white transition-all duration-300 hover:scale-110"
                data-testid="footer-instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">{t("contactTitle")}</div>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to={lh("/") + "#kontakt"} className="inline-flex items-center gap-2 text-zinc-700 dark:text-zinc-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors" data-testid="footer-contact-link">
                  <span>{t("contact")}</span>
                </Link>
              </li>
              <li>
                <a
                  href={mailto}
                  className="inline-flex items-center gap-2 text-zinc-700 dark:text-zinc-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors break-all"
                  data-testid="footer-email-link"
                >
                  <Mail className="h-4 w-4 text-purple-500 shrink-0" />
                  <span>{COMPANY.publicEmail}</span>
                </a>
              </li>
              <li>
                <a
                  href={COMPANY.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-zinc-700 dark:text-zinc-300 hover:text-pink-600 dark:hover:text-pink-400 transition-colors"
                  data-testid="footer-instagram-link"
                >
                  <Instagram className="h-4 w-4 text-pink-500" />
                  <span>@{COMPANY.instagramHandle}</span>
                </a>
              </li>
            </ul>
          </div>

          <nav className="text-sm" aria-label="Footer">
            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">{lang === "de" ? "Service" : lang === "tr" ? "Servis" : "Service"}</div>
            <ul className="space-y-2.5 text-zinc-700 dark:text-zinc-300">
              <li><Link to={lh("/pricing")} className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors" data-testid="footer-pricing">{t("pricing")}</Link></li>
              <li><Link to="/login" className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors" data-testid="footer-login">{t("login")}</Link></li>
              <li><Link to={lh("/impressum")} className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors" data-testid="footer-impressum">{t("impressum")}</Link></li>
              <li><Link to={lh("/datenschutz")} className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors" data-testid="footer-datenschutz">{t("privacy")}</Link></li>
            </ul>
          </nav>

          <nav className="text-sm" aria-label="Footer cities" data-testid="footer-cities">
            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3 flex items-center gap-1.5">
              <MapPin className="h-3 w-3" />
              {citiesLabel}
            </div>
            <ul className="space-y-2.5 text-zinc-700 dark:text-zinc-300">
              {FOOTER_CITIES.map((c) => (
                <li key={c.id}>
                  <Link
                    to={c.path}
                    className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                    data-testid={`footer-city-${c.id}`}
                  >
                    {c.localKeyword}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-10 pt-6 border-t border-zinc-100 dark:border-zinc-900 text-xs text-zinc-500 dark:text-zinc-500 flex flex-col sm:flex-row gap-2 sm:justify-between">
          <span>© {new Date().getFullYear()} {COMPANY.legalName} · {COMPANY.city}</span>
          <span>Made in Europe 🇪🇺</span>
        </div>
      </div>
    </footer>
  );
}
